/**
 * API service layer for cityswell-booking.
 * All network calls go through here — Picker.js should not import axios directly.
 *
 * Base URL: https://api.cityswell.com.ua/api
 *
 * Response shapes are documented per function via JSDoc.
 */

import axios from 'axios';
import { API_BASE, TELEGRAM_BOT_API } from '../constants';

/**
 * @typedef {{ startTime: string, endTime: string, coachPrice: number, boatPrice: number }} TimeFrame
 * @typedef {{ id: string, name: string }} NamedEntity
 */

/**
 * Fetch the booking calendar (enabled date range + disabled days).
 * @returns {Promise<{ lastDay: string, disabledDays: string[] }>}
 */
export async function fetchCalendar() {
    const res = await axios.get(`${API_BASE}/calendar`);
    return res.data;
}

/**
 * Fetch all available boats.
 * @returns {Promise<{ ships: NamedEntity[] }>}
 */
export async function fetchShips() {
    const res = await axios.get(`${API_BASE}/ship`);
    return res.data;
}

/**
 * Fetch coaches available on a given day.
 * @param {string} day - ISO date string "YYYY-MM-DD"
 * @returns {Promise<{ coaches: NamedEntity[] }>}
 */
export async function fetchCoaches(day) {
    const res = await axios.post(`${API_BASE}/coach`, { day });
    return res.data;
}

/**
 * Find available session time frames for the given booking parameters.
 * Also returns whether the rider has an existing account and their balance.
 *
 * @param {{ day: string, shipID: string, coachID: string, ridersQty: number, beginnersQty: number, name?: string, phone?: string, slots?: string[], sessionDuration?: number }} params
 * @returns {Promise<{ timeFrames: TimeFrame[], isFound: boolean, balance: number }>}
 */
export async function findSessions(params) {
    const res = await axios.post(`${API_BASE}/session`, params);
    return res.data;
}

/**
 * Create a booking order.
 * Returns orderId = 0 on failure.
 *
 * @param {{ phone: string, startDate: string, shipId: string, coachId: string, sessionDuration: number, ridersQty: number, name: string, comment: string, beginnersQty: number }} order
 * @returns {Promise<{ orderId: number }>}
 */
export async function createBooking(order) {
    const res = await axios.post(`${API_BASE}/book`, order);
    return res.data;
}

/**
 * Create a Monobank payment invoice.
 *
 * @param {{ amount: number, destination: string, reference: string, redirectUrl: string, webhookUrl: string }} params
 * @param {string} monobankToken
 * @returns {Promise<{ pageUrl: string }>}
 */
export async function createMonobankInvoice({ amount, destination, reference, redirectUrl, webhookUrl }, monobankToken) {
    const res = await axios.post(
        'https://api.monobank.ua/api/merchant/invoice/create',
        {
            amount,
            ccy: 980, // UAH
            merchantPaymInfo: { destination, reference },
            redirectUrl,
            validity: 3600,
            webHookUrl: webhookUrl,
        },
        { headers: { 'X-Token': monobankToken } }
    );
    return res.data;
}

/**
 * Fetch Telegram contact data server-side (validates initData signature).
 * @param {string} initData - raw Telegram WebApp.initData string
 * @returns {Promise<{ phone: string, first_name: string, last_name?: string }>}
 */
export async function fetchTelegramContact(initData) {
    const res = await axios.post(`${TELEGRAM_BOT_API}/api/contact`, { init_data: initData });
    return res.data;
}
